/* eslint-disable @typescript-eslint/naming-convention */
import * as IoC from 'typescript-ioc';
import { action, observable, runInAction } from 'mobx';
import { IntlShape } from 'react-intl';
import { ConnectionType, IConfigurePassportDisplayDTO, IConfigurePassportDisplayParams, IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../api/connections.dao';
import type { IConfigurePassportDisplayProps } from './configure-passport-display.container';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { FormFieldProps, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../infrastructure/implementations/form-viewmodel-controller';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { configList, ConfigurePassportDisplayFieldKey, CONNECTION_FIELDS, PassportConfigurationMode } from './configure-passport-display.config';
import { IEventRequestParams } from '../../../../../api/shared.dto';
import { isTrackingFieldChanged, isTrackingFieldEnabled, isTrackingFieldPopulated, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { getConnectionItemLabelText, getConnectionTemplateItemLabelText } from '../../../../../infrastructure/utils/connections.utils';
import { NOT_AVAILABLE_STRING } from '../../../../../constants/common.constants';
import { IPhoneNumbersSet, IZoomDAO } from '../../../../../api/zoom.dao';
import { TConnection } from '../../../../../api/blueprints/connection.template.types';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../global/rich-text-editor/utils/transpiler';

export interface IConfigurePassportDisplayFormProps extends IEventRequestParams {
    meetingConnectionId: number;
    roomId?: number;
    meetingId?: number;
    blueprintId?: string;
    blueprintConnectionId?: string;
}

interface ITrackingConfigurePassportDisplayValues {
    'Account Name': string;
    'Account ID': number;
    'Event Name': string;
    'Event ID': number;
    'Meeting ID'?: number;
    'Room ID'?: number;
    'Connection ID'?: number;
    'Connection Type': string;
    'Passport Label': string;
    'Passport Description': string;
    'Connect Button Label'?: string;
    'Show/Hide button'?: string;
    'Show Dial-In Info'?: string;
    'Hide Dial-In Info'?: string;
}

export const defaultPassportPreviewValues: IConfigurePassportDisplayDTO = {
    passportLabel: '',
    passportDescription: '',
    connectionDetailsEnabled: true,
    connectButtonLabel: 'Connect to Meeting',
    showHideButtonEnabled: true,
    showDialInInfo: 'Show Dial-In Info',
    hideDialInInfo: 'Hide Dial-In Info',
};

export default class ConfigurePassportDisplayVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    protected static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'ConfigurePassportDisplayVM', configList);
            }
        }();
    }

    @observable
    private formDataCtrl: AbstractFormViewModelController;
    @observable
    public initialData: IConfigurePassportDisplayDTO;
    @observable
    public isLoading: boolean = false;
    @observable
    public isSaving: boolean = false;
    @observable
    public isPassportPreviewModalOpen: boolean;
    private eventParams: IEventRequestParams;
    public meetingConnectionPassportConfigurationParams: IConfigurePassportDisplayParams;
    public connectionType: string;
    public connectionLabel: string;
    public connection: IConnectionDetailsDTO;
    public phoneNumbersSet: IPhoneNumbersSet[];
    public passportConfigurationMode: PassportConfigurationMode;
    public blueprintPassportConfiguration: IConfigurePassportDisplayDTO;
    public editBlueprintConnection: (item: TConnection) => void;

    constructor(
        configurePassportDisplayProps: IConfigurePassportDisplayProps,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.passportConfigurationMode = configurePassportDisplayProps.passportConfigurationMode;
        this.initialData = defaultPassportPreviewValues;
        this.connectionType = configurePassportDisplayProps.connection.type;
        this.connection = configurePassportDisplayProps.connection;
        this.phoneNumbersSet = [];

        if (this.passportConfigurationMode === PassportConfigurationMode.MeetingConnection) {
            this.eventParams = {
                accountId: configurePassportDisplayProps.props.accountId,
                eventId: configurePassportDisplayProps.props.eventId
            };

            this.meetingConnectionPassportConfigurationParams = {
                meetingConnectionId: configurePassportDisplayProps.props.meetingConnectionId,
                roomId: configurePassportDisplayProps.props?.roomId || null,
                meetingId: configurePassportDisplayProps.props?.meetingId || null,
                joinMethodId: configurePassportDisplayProps.connection.joinMethodId || null,
            };
            this.connectionLabel = getConnectionItemLabelText(configurePassportDisplayProps.connection);
        } else if (this.passportConfigurationMode === PassportConfigurationMode.ConnectionTemplate) {
            this.editBlueprintConnection = configurePassportDisplayProps.editBlueprintConnection;
            this.blueprintPassportConfiguration = configurePassportDisplayProps?.blueprintPassportConfiguration || this.initialData;
            this.connectionLabel = getConnectionTemplateItemLabelText(configurePassportDisplayProps.connection as TConnection);
        }

        this.initialize();
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    public get formVM(): AbstractFormViewModelController { return this.formDataCtrl; }

    @action
    public initialize = (): void => {
        if (this.passportConfigurationMode === PassportConfigurationMode.MeetingConnection) {
            this.getConfigurePassportDisplay();
        } else if (this.passportConfigurationMode === PassportConfigurationMode.ConnectionTemplate) {
            const props = this.composeFormVMInitialProps(this.blueprintPassportConfiguration);
            this.initVM(props);
        }
        if (!this.connection.participantTelephoneNumbers && this.connectionType === ConnectionType.AudioBridge) {
            this.getPhoneNumbersSet();
        }
    };

    @action
    public initVM = (props: InitialData): void => {
        this.formDataCtrl = ConfigurePassportDisplayVM.createFormVM(props);
        this.formDataCtrl.setValue(ConfigurePassportDisplayFieldKey.CONNECTION_TYPE, this.connectionType);
    };

    public getFormFieldPropsByKey = (key: string): FormFieldProps => {
        return {
            value: this.formDataCtrl?.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl?.getValueValidationState(key),
        } as FormFieldProps;
    };

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.getFormFieldPropsByKey(key);
        }
        return formFields;
    };

    private composeFormVMInitialProps = (response?: IConfigurePassportDisplayDTO): InitialData => {
        return {
            [ConfigurePassportDisplayFieldKey.PASSPORT_LABEL]: response?.passportLabel,
            [ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION]: transpileInlineStylesToClasses(response?.passportDescription),
            [ConfigurePassportDisplayFieldKey.CONNECT_BUTTON_LABEL]: response?.connectButtonLabel || 'Connect to Meeting',
            [ConfigurePassportDisplayFieldKey.CONNECT_DETAILS_ENABLED]: response?.connectionDetailsEnabled,
            [ConfigurePassportDisplayFieldKey.SHOW_HIDE_BUTTON_ENABLED]: response?.showHideButtonEnabled,
            [ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO]: response?.showDialInInfo,
            [ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO]: response?.hideDialInInfo,
        };
    };

    public submitForm = (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.updateForm();
        }
    };

    public updateForm = (): Promise<boolean> => {
        if (this.passportConfigurationMode === PassportConfigurationMode.MeetingConnection) {
            return this.updateConfigurePassportDisplay();
        } else {
            const dto = { ...this.connection, ...{ passportConfiguration: this.getConfigurePassportDisplayDTO() } };
            this.editBlueprintConnection(dto as TConnection);
            return Promise.resolve(true);
        }
    };

    public updateConfigurePassportDisplay = async (): Promise<boolean> => {
        const { roomId, joinMethodId } = this.meetingConnectionPassportConfigurationParams;
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            const dto = {
                ...this.toApiDTO(),
                ...(joinMethodId && { joinMethodId: joinMethodId }),
                passportDescription: transpileClassesToInlineStyles(this.toApiDTO().passportDescription)
            };

            if (roomId) {
                await this.connectionsDAO.updateRoomConnectionPassportConfigureDisplay(dto);
            } else {
                await this.connectionsDAO.updateConnectionPassportConfigureDisplay(dto);
            }

            showNotification(this.intl.formatMessage({
                id: 'oe.connections.passportDisplayUpdated',
                description: 'Notification about passport display',
                defaultMessage: 'Passport display updated',
            }));

            this.trackingService.get().track(
                TRACKING_EVENTS.CONFIGURE_PASSPORT_DISPLAY,
                this.getTrackingData()
            );
            runInAction(() => {
                this.isSaving = false;
            });
            return true;

        } catch (e) {
            ConfigurePassportDisplayVM.handleApiException(e);
            runInAction(() => {
                this.isSaving = false;
            });
            return false;
        }
    };

    private toApiDTO = (): IEventRequestParams & IConfigurePassportDisplayParams & IConfigurePassportDisplayDTO => {
        const dto = { ...this.toMeetingConnectionAPIParams(), ...this.getConfigurePassportDisplayDTO() };
        return dto;
    };

    private getConfigurePassportDisplayDTO = (): IConfigurePassportDisplayDTO => {
        const dto = {} as IConfigurePassportDisplayDTO;
        this.formDataCtrl.getVmState().forEach(({ name, value }) => {
            if (this.connectionType === ConnectionType.AudioBridge) {
                if (CONNECTION_FIELDS.audioBridge.includes(name as ConfigurePassportDisplayFieldKey)) {
                    dto[name] = value;
                }
            } else if (this.connectionType === ConnectionType.Knovio) {
                if (CONNECTION_FIELDS.knovio.includes(name as ConfigurePassportDisplayFieldKey)) {
                    dto[name] = value;
                }
            } else {
                if (CONNECTION_FIELDS.others.includes(name as ConfigurePassportDisplayFieldKey)) {
                    if (name === ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION) {
                        dto[name] = transpileClassesToInlineStyles(value as string);
                    } else {
                        dto[name] = value;
                    }
                }
            }
        });

        return dto;
    };

    @action
    public getConfigurePassportDisplay = async (): Promise<void> => {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const dto = { ...this.toMeetingConnectionAPIParams()};
            const response = this.meetingConnectionPassportConfigurationParams.roomId
                ? await this.connectionsDAO.getRoomConnectionPassportConfigureDisplay(dto)
                : await this.connectionsDAO.getConnectionPassportConfigureDisplay(dto);

            const responseWithTranspiledFields = {
                ...response,
                passportDescriptio: transpileInlineStylesToClasses(response.passportDescription)
            };

            const props = this.composeFormVMInitialProps(responseWithTranspiledFields);

            runInAction(() => {
                this.initialData = responseWithTranspiledFields;
                this.initVM(props);
            });

        } catch (e) {
            if (e?.response?.status !== 404) {
                ConfigurePassportDisplayVM.handleApiException(e);
            }
            runInAction(() => {
                const props = this.composeFormVMInitialProps(this.initialData);
                this.initVM(props);
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    private toMeetingConnectionAPIParams = (): IEventRequestParams & IConfigurePassportDisplayParams => {
        const { roomId, meetingId, meetingConnectionId } = this.meetingConnectionPassportConfigurationParams;
        const { accountId, eventId } = this.eventParams;

        const roomMeetingId = roomId ? { roomId: roomId } : { meetingId: meetingId };
        return {
            accountId: accountId,
            eventId: eventId,
            meetingConnectionId: meetingConnectionId,
            ...roomMeetingId
        };
    };

    @action
    public setShowHideDialToDefaultValues = (): void => {
        this.formDataCtrl.setValue(ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO, this.initialData.showDialInInfo);
        this.formDataCtrl.setValue(ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO, this.initialData.hideDialInInfo);
    };

    @action
    public setPassportPreviewOpen = (value: boolean): void => {
        this.isPassportPreviewModalOpen = value;
    };

    public getTrackingData = (): ITrackingConfigurePassportDisplayValues => {
        const { roomId, meetingId, meetingConnectionId } = this.meetingConnectionPassportConfigurationParams;

        const passportLabelChanged = isTrackingFieldChanged(
            this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.PASSPORT_LABEL) !== this.initialData.passportLabel
        );

        const passportLabelPopulated =
            isTrackingFieldPopulated(this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.PASSPORT_LABEL) as string);

        const passportDescriptionChanged = isTrackingFieldChanged(
            this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION) !== this.initialData.passportDescription
        );

        const passportDescriptionPopulated = isTrackingFieldPopulated(
            this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION) as string
        );


        const trackingData = {
            'Account ID': this.eventParams.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Event ID': this.eventParams.eventId,
            'Event Name': this.globalStorage.getEvent()?.name,
            ...(meetingId && { 'Meeting ID': meetingId }),
            ...(roomId && { 'Room ID': roomId }),
            'Connection ID': meetingConnectionId,
            'Connection Type': this.connectionType,
            'Passport Label': passportLabelChanged + ',' + passportLabelPopulated,
            'Passport Description': passportDescriptionChanged + ',' + passportDescriptionPopulated,
        };

        if (this.connectionType === ConnectionType.AudioBridge) {
            const isShowHideButtonEnabled = !!this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.SHOW_HIDE_BUTTON_ENABLED);
            let showDialInInfoChanged = NOT_AVAILABLE_STRING;
            let hideDialInInfoChanged = NOT_AVAILABLE_STRING;
            if (isShowHideButtonEnabled) {
                showDialInInfoChanged = isTrackingFieldChanged(
                    this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO) !== this.initialData.showDialInInfo
                );
                hideDialInInfoChanged = isTrackingFieldChanged(
                    this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO) !== this.initialData.hideDialInInfo
                );
            }
            return {
                ...trackingData,
                'Show/Hide button': isTrackingFieldEnabled(isShowHideButtonEnabled),
                'Show Dial-In Info': showDialInInfoChanged,
                'Hide Dial-In Info': hideDialInInfoChanged,
            };
        } else if (this.connectionType === ConnectionType.Knovio) {
            return trackingData;
        } else {
            const connectButtonLabelChanged = isTrackingFieldChanged(
                this.formDataCtrl.getValue(ConfigurePassportDisplayFieldKey.CONNECT_BUTTON_LABEL) !== this.initialData.connectButtonLabel
            );
            return { ...trackingData, 'Connect Button Label': connectButtonLabelChanged };
        }

    };

    @action
    public getPhoneNumbersSet = async (): Promise<void> => {
        if (this.connection.audioProviderId) {
            try {
                const response = await this.zoomDAO.getPhoneNumbersSet({ audioProviderId: this.connection.audioProviderId });
                this.phoneNumbersSet = response.telephoneNumbers;
            } catch (e) {
                ConfigurePassportDisplayVM.handleApiException(e);
            }
        }
    };
}
